import { useEffect, useState, useMemo } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Drawer,
  IconButton,
} from "@mui/material";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import {
  MdDashboardCustomize,
  MdOutlinePayment,
  MdOutlinePlaylistAddCheckCircle,
  MdOutlineCheckBox,
  MdOutlineExpandMore,
} from "react-icons/md";
import { TbLogout, TbCalendarFilled } from "react-icons/tb";
import { SiPlatformdotsh } from "react-icons/si";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  getAdminSession,
  selectGetAdminSession,
} from "features/admin/presentation/slices/get-admin-session.slice";
import {
  LogoutAdminState,
  logoutAdmin,
  resetLogoutAdmin,
  selectLogoutAdmin,
} from "features/admin/presentation/slices/logout-admin.slice";
import {
  BsFillPersonFill,
  BsListTask,
  BsArrowReturnLeft,
} from "react-icons/bs";
import {
  selectMs6SideBar,
  toggleMs6SideBar,
} from "../../slices/ms6-sidebar.slice";
import { FaAngleDown, FaAngleUp, FaHistory, FaBars } from "react-icons/fa";
import { FaWpforms, FaPeopleGroup } from "react-icons/fa6";
import { GiGears } from "react-icons/gi";
import { AiOutlineBlock } from "react-icons/ai";
import { HiOutlineSpeakerphone } from "react-icons/hi";
import { LiaMoneyBillWaveSolid } from "react-icons/lia";
import { RiHomeSmileLine } from "react-icons/ri";
import { BiUser } from "react-icons/bi";
import React from "react";

const drawerWidth = "17rem";

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `5rem`,
});

const Drawers = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

function Ms6DrawerMenu() {
  const ms6Sidebar = useAppSelector(selectMs6SideBar);
  const getLogoutAdminState = useAppSelector(selectLogoutAdmin);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const [manpowerExpanded, setManpowerExpanded] = useState(false);
  const [materialExpanded, setMaterialExpanded] = useState(false);

  // Fetch admin session on component mount
  useEffect(() => {
    dispatch(getAdminSession());
  }, [dispatch]);

  const payable = getAdminSessionState.data?.admin.user_details.sos_groups.some(
    (group) => group.id >= 7 && group.id <= 9
  );

  // Get the number of stores associated with the account
  const userStores =
    getAdminSessionState.data?.admin?.user_details?.stores || [];

  // Define menu items, with "Form" enable state based on number of stores
  const menuitems = useMemo(
    () => [
      {
        text: "Dashboard Page",
        path: "dashboard-page",
        icon: <MdDashboardCustomize size={18} />,
        enable: true,
      },
      {
        text: "Dashboard",
        path: "dashboard",
        icon: <MdDashboardCustomize size={18} />,
        enable: false,
      },
      {
        text: "Form",
        path: "form-list",
        icon: <TbCalendarFilled size={18} />,
        enable: userStores.length <= 1, // Enable only if 1 or fewer stores
      },
      {
        text: "Machines",
        path: "machines",
        icon: <GiGears size={18} />,
        enable: true,
      },
      {
        text: "Methods",
        path: "methods",
        icon: <MdOutlinePlaylistAddCheckCircle size={18} />,
        enable: true,
      },
      {
        text: "Marketing",
        path: "marketing",
        icon: <HiOutlineSpeakerphone size={18} />,
        enable: true,
      },
      {
        text: "Money",
        path: "money",
        icon: <LiaMoneyBillWaveSolid size={18} />,
        enable: true,
      },
      {
        text: "Checklist Builder",
        path: "checklist-builder",
        icon: <MdOutlineCheckBox size={18} />,
        enable: true,
      },
    ],
    [userStores.length]
  );

  const manpowerMenuItems = [
    {
      text: "Daily Attendance",
      path: "manpower/daily-attendance",
      icon: <TbCalendarFilled size={16} />,
      enable: true,
    },
    {
      text: "Incident Reports",
      path: "manpower/incident-report",
      icon: <BsListTask size={16} />,
      enable: true,
    },
  ];

  const materialMenuItems = [
    {
      text: "Dashboard",
      path: "material/dashboard",
      icon: <MdDashboardCustomize size={16} />,
      enable: false,
    },
    {
      text: "Inventory Levels",
      path: "material/inventory-levels",
      icon: <FaPeopleGroup size={16} />,
      enable: true,
    },
    {
      text: "Efficiency Metrics",
      path: "material/efficiency-metric",
      icon: <TbCalendarFilled size={16} />,
      enable: true,
    },
    {
      text: "Purchase Requisition",
      path: "material/purchase-requisition",
      icon: <GiGears size={16} />,
      enable: true,
    },
  ];

  useEffect(() => {
    if (getLogoutAdminState.status === LogoutAdminState.success) {
      dispatch(getAdminSession());
      dispatch(resetLogoutAdmin());
      navigate("/admin");
    }
  }, [getLogoutAdminState, dispatch, navigate]);

  return (
    <div className="space-y-2">
      <nav>
        <ul className="space-y-1">
          {/* Main Menu Items */}
          {menuitems.map((item, index) => {
            const { text, path, icon, enable } = item;
            const key = index;
            return (
              <React.Fragment key={key}>
                {enable && (
                  <li>
                    <NavLink
                      to={path}
                      end
                      className={(navData) =>
                        navData.isActive
                          ? `flex items-center rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg transition-all duration-200 ${
                              !ms6Sidebar.status ? "p-3 justify-center" : "p-3"
                            }`
                          : `flex items-center rounded-lg text-slate-300 hover:bg-slate-700 hover:text-white transition-all duration-200 group ${
                              !ms6Sidebar.status ? "p-3 justify-center" : "p-3"
                            }`
                      }
                    >
                      <div
                        className={`flex items-center ${
                          !ms6Sidebar.status ? "" : "space-x-3"
                        }`}
                      >
                        <span className="flex-shrink-0 group-hover:text-red-400 transition-colors duration-200">
                          {icon}
                        </span>
                        <span
                          className={`font-medium transition-all duration-300 ${
                            !ms6Sidebar.status
                              ? "opacity-0 w-0 overflow-hidden ml-0"
                              : "opacity-100 ml-3"
                          }`}
                        >
                          {text}
                        </span>
                      </div>
                    </NavLink>
                  </li>
                )}
              </React.Fragment>
            );
          })}

          {/* Manpower Accordion */}
          <li className="mt-2">
            <Accordion
              onChange={(event, isExpanded) => {
                setManpowerExpanded(isExpanded);
              }}
              sx={{
                color: "white",
                borderRadius: "8px",
                backgroundColor: "rgba(51, 65, 85, 0.6)",
                boxShadow: "none",
                border: "1px solid rgba(71, 85, 105, 0.5)",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<MdOutlineExpandMore color="white" size={20} />}
                sx={{
                  paddingX: ms6Sidebar.status ? "12px" : "12px",
                  minHeight: "48px",
                  "&.Mui-expanded": {
                    minHeight: "48px",
                  },
                }}
              >
                <div
                  className={`flex items-center ${
                    !ms6Sidebar.status ? "" : "space-x-3"
                  }`}
                >
                  <span className="flex-shrink-0">
                    <FaPeopleGroup color="white" size={18} />
                  </span>
                  <span
                    className={`font-medium transition-all duration-300 ${
                      !ms6Sidebar.status
                        ? "opacity-0 w-0 overflow-hidden ml-0"
                        : "opacity-100 ml-3"
                    }`}
                  >
                    Manpower
                  </span>
                </div>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <div className="space-y-1 p-2">
                  {manpowerMenuItems.map((item, index) => {
                    const { text, path, icon, enable } = item;
                    const key = index;
                    return (
                      <React.Fragment key={key}>
                        {enable && (
                          <NavLink
                            to={path}
                            className={(navData) =>
                              navData.isActive
                                ? `flex items-center rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg transition-all duration-200 ${
                                    !ms6Sidebar.status
                                      ? "p-2 justify-center"
                                      : "p-2"
                                  }`
                                : `flex items-center rounded-lg text-slate-300 hover:bg-slate-600 hover:text-white transition-all duration-200 group ${
                                    !ms6Sidebar.status
                                      ? "p-2 justify-center"
                                      : "p-2"
                                  }`
                            }
                          >
                            <div
                              className={`flex items-center ${
                                !ms6Sidebar.status ? "" : "space-x-3"
                              }`}
                            >
                              <span className="flex-shrink-0 group-hover:text-red-400 transition-colors duration-200">
                                {icon}
                              </span>
                              <span
                                className={`text-sm font-medium transition-all duration-300 ${
                                  !ms6Sidebar.status
                                    ? "opacity-0 w-0 overflow-hidden ml-0"
                                    : "opacity-100 ml-3"
                                }`}
                              >
                                {text}
                              </span>
                            </div>
                          </NavLink>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </AccordionDetails>
            </Accordion>
          </li>

          {/* Materials Accordion */}
          <li className="mt-2">
            <Accordion
              onChange={(event, isExpanded) => {
                setMaterialExpanded(isExpanded);
              }}
              sx={{
                color: "white",
                borderRadius: "8px",
                backgroundColor: "rgba(51, 65, 85, 0.6)",
                boxShadow: "none",
                border: "1px solid rgba(71, 85, 105, 0.5)",
                "&:before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<MdOutlineExpandMore color="white" size={20} />}
                sx={{
                  paddingX: ms6Sidebar.status ? "12px" : "12px",
                  minHeight: "48px",
                  "&.Mui-expanded": {
                    minHeight: "48px",
                  },
                }}
              >
                <div
                  className={`flex items-center ${
                    !ms6Sidebar.status ? "" : "space-x-3"
                  }`}
                >
                  <span className="flex-shrink-0">
                    <AiOutlineBlock color="white" size={18} />
                  </span>
                  <span
                    className={`font-medium transition-all duration-300 ${
                      !ms6Sidebar.status
                        ? "opacity-0 w-0 overflow-hidden ml-0"
                        : "opacity-100 ml-3"
                    }`}
                  >
                    Materials
                  </span>
                </div>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <div className="space-y-1 p-2">
                  {materialMenuItems.map((item, index) => {
                    const { text, path, icon, enable } = item;
                    const key = index;
                    return (
                      <React.Fragment key={key}>
                        {enable && (
                          <NavLink
                            to={path}
                            className={(navData) =>
                              navData.isActive
                                ? `flex items-center rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg transition-all duration-200 ${
                                    !ms6Sidebar.status
                                      ? "p-2 justify-center"
                                      : "p-2"
                                  }`
                                : `flex items-center rounded-lg text-slate-300 hover:bg-slate-600 hover:text-white transition-all duration-200 group ${
                                    !ms6Sidebar.status
                                      ? "p-2 justify-center"
                                      : "p-2"
                                  }`
                            }
                          >
                            <div
                              className={`flex items-center ${
                                !ms6Sidebar.status ? "" : "space-x-3"
                              }`}
                            >
                              <span className="flex-shrink-0 group-hover:text-red-400 transition-colors duration-200">
                                {icon}
                              </span>
                              <span
                                className={`text-sm font-medium transition-all duration-300 ${
                                  !ms6Sidebar.status
                                    ? "opacity-0 w-0 overflow-hidden ml-0"
                                    : "opacity-100 ml-3"
                                }`}
                              >
                                {text}
                              </span>
                            </div>
                          </NavLink>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </AccordionDetails>
            </Accordion>
          </li>
        </ul>
      </nav>
    </div>
  );
}

/**
 * =========
 * LOGOUT BUTTON COMPONENT
 * =====
 */
function LogoutButton() {
  const ms6SideBarState = useAppSelector(selectMs6SideBar);
  const dispatch = useAppDispatch();

  return (
    <button
      onClick={() => {
        dispatch(logoutAdmin());
      }}
      className={`w-full group relative overflow-hidden rounded-xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 transition-all duration-300 ${
        !ms6SideBarState.status ? "p-3" : "p-4"
      }`}
    >
      <div className="absolute inset-0 bg-white opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
      <div
        className={`relative flex items-center ${
          !ms6SideBarState.status ? "justify-center" : "space-x-3"
        }`}
      >
        <div className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 rounded-lg">
          <TbLogout size={18} className="text-white" />
        </div>
        <div
          className={`text-left transition-all duration-300 ${
            !ms6SideBarState.status
              ? "opacity-0 w-0 overflow-hidden"
              : "opacity-100"
          }`}
        >
          <div className="text-sm font-semibold text-white">Sign Out</div>
          <div className="text-xs text-red-100">End your session</div>
        </div>
      </div>
    </button>
  );
}

const SideBarContent = () => {
  const ms6Sidebar = useAppSelector(selectMs6SideBar);
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return (
    <Box className="relative h-screen bg-gradient-to-b from-slate-800 to-slate-900 font-['Varela_Round'] duration-500 z-10 shadow-2xl flex flex-col">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0 bg-gradient-to-r from-slate-800 to-slate-700 border-b border-slate-600">
        <DrawerHeader className="!min-h-[0px] px-4 py-6">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <img
                  src={require("assets/favicon.png")}
                  className="w-10 h-10 bg-white border-2 rounded-lg shadow-lg border-red-500 transition-all duration-300"
                  alt="6Ms System Logo"
                />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div
                className={`transition-all duration-300 ${
                  !ms6Sidebar.status
                    ? "opacity-0 w-0 overflow-hidden"
                    : "opacity-100"
                }`}
              >
                <h1 className="text-lg font-bold text-white">6Ms System</h1>
                <p className="text-xs text-slate-300">Admin Dashboard</p>
              </div>
            </div>
            <button
              onClick={() => {
                dispatch(toggleMs6SideBar());
              }}
              className={`p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-600 transition-all duration-200 ${
                !ms6Sidebar.status ? "ml-0" : "ml-auto"
              }`}
            >
              <FaBars size={16} />
            </button>
          </div>
        </DrawerHeader>
      </div>

      {/* Scrollable Content Container */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {/* Return to Landing Section */}
          <div className="px-4 py-4 border-b border-slate-600">
            <button
              onClick={() => {
                navigate("/admin/landing");
              }}
              className={`w-full group relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-300 ${
                !ms6Sidebar.status ? "p-3" : "p-4"
              }`}
            >
              <div className="absolute inset-0 bg-white opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
              <div
                className={`relative flex items-center ${
                  !ms6Sidebar.status ? "justify-center" : "space-x-3"
                }`}
              >
                <div className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 rounded-lg">
                  <RiHomeSmileLine size={18} className="text-white" />
                </div>
                <div
                  className={`text-left transition-all duration-300 ${
                    !ms6Sidebar.status
                      ? "opacity-0 w-0 overflow-hidden"
                      : "opacity-100"
                  }`}
                >
                  <div className="text-sm font-semibold text-white">
                    Return to Home
                  </div>
                  <div className="text-xs text-blue-100">Main menu page</div>
                </div>
              </div>
            </button>
          </div>

          {/* User Info Section */}
          <div className="px-4 py-4 border-b border-slate-600">
            <div
              className={`transition-all duration-300 ${
                !ms6Sidebar.status
                  ? "opacity-0 h-0 overflow-hidden"
                  : "opacity-100"
              }`}
            >
              <div className="bg-slate-700 bg-opacity-50 rounded-xl p-4 border border-slate-600">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center">
                    <BiUser size={20} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {getAdminSessionState.data?.admin.user_details
                        .first_name +
                        " " +
                        getAdminSessionState.data?.admin.user_details.last_name}
                    </div>
                    {getAdminSessionState.data?.admin.user_details.company !==
                      null && (
                      <div className="text-xs text-slate-300">
                        {
                          getAdminSessionState.data?.admin.user_details.company
                            .name
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <div className="px-4 py-4 pb-6">
            <Ms6DrawerMenu />
          </div>
        </div>

        {/* Fixed Footer with Logout - Always at Bottom */}
        <div className="flex-shrink-0 bg-gradient-to-r from-slate-800 to-slate-700 border-t border-slate-600 px-4 py-4">
          <LogoutButton />
        </div>
      </div>
    </Box>
  );
};

function Ms6Drawer() {
  const ms6Sidebar = useAppSelector(selectMs6SideBar);
  const dispatch = useAppDispatch();

  return (
    <>
      <div className="md:hidden">
        <Drawer
          className="md:hidden"
          anchor={"left"}
          open={ms6Sidebar.status}
          onClose={() => {
            dispatch(toggleMs6SideBar());
          }}
        >
          <SideBarContent />
        </Drawer>
      </div>

      <div className="hidden md:flex">
        <Drawers
          anchor="left"
          variant="permanent"
          sx={{ zIndex: 10 }}
          open={ms6Sidebar.status}
          onClose={() => {
            dispatch(toggleMs6SideBar());
          }}
        >
          <SideBarContent />
        </Drawers>
      </div>
    </>
  );
}

export function Ms6SidebarWrapper() {
  const location = useLocation();

  useEffect(() => {
    document.getElementById("audit-main-section")?.scroll(0, 0);
  }, [location]);

  return (
    <main className="flex min-h-screen">
      <Ms6Drawer />
      <section
        id="audit-main-section"
        className="flex-1 h-screen overflow-y-auto bg-paper"
      >
        <Outlet />
      </section>
    </main>
  );
}
